import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientProfile from '../../Components/Organizations/ClientProfile';
import Documents from '../../Components/Organizations/Documents';
import TransitionInfo from '../../Components/Organizations/TransitionInfo';
import SOP from '../../Components/Organizations/SOP';
import Reports from '../../Components/Organizations/Reports';
import OrganizationFormModal from '../../Components/Organizations/OrganizationFormModal';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { validateNpiNumber, getOrganizationById } from '../../Services/OrganizationService';
import { MdCheck } from 'react-icons/md';
import { toast } from 'react-toastify';
import DotLottie from '../../Components/Common/DotLottie';

const TAB_ITEMS = [
    { id: 'client-profile', label: 'Client Profile' },
    { id: 'documents', label: 'Documents' },
    { id: 'transition-info', label: 'Transition Info' },
    { id: 'sop', label: 'SOP' },
    { id: 'reports', label: 'Reports' },
];

export default function OrganizationForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);
    const isCreateMode = !isEditing;
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [completedTabs, setCompletedTabs] = React.useState([]);
    const [npiNumber, setNpiNumber] = React.useState('');
    const [isNpiValidated, setIsNpiValidated] = React.useState(!isCreateMode);
    const [validationLoading, setValidationLoading] = React.useState(false);
    const [validationError, setValidationError] = React.useState('');
    const [organizationId, setOrganizationId] = React.useState(id || null);
    const [prepopulatedData, setPrepopulatedData] = React.useState(null);

    const activeTabId = TAB_ITEMS[activeTabIndex]?.id;
    const isLastTab = activeTabIndex === TAB_ITEMS.length - 1;
    const isFirstTab = activeTabIndex === 0;


    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );

    const canCreateOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_CREATE, decodedToken);
    const canEditOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_EDIT, decodedToken);

    const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

    const formatNpiInput = (value) => {
        const digits = normalizeDigits(value).slice(0, 10);
        if (!digits) return '';
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handleValidateNpi = React.useCallback(async () => {
        setValidationError('');
        const trimmedNpi = npiNumber.trim();

        if (!trimmedNpi) {
            setValidationError('NPI Number is required to continue.');
            return;
        }

        setValidationLoading(true);
        try {
            const response = await validateNpiNumber(trimmedNpi, token);
            if (response?.is_success) {
                const data = response.data || null;
                // Match every casing getOrganizationById's own id resolution below checks —
                // otherwise a match against an already-existing org can prefill the form with
                // real data while organizationId stays null, and the next save creates a dupe.
                // Organization.id is a UUID on the backend — never Number() it.
                const resolvedId = data?.Id ?? data?.id ?? data?.organization_id ?? data?.OrganizationId ?? data?.org_id ?? data?.OrgId ?? null;
                setPrepopulatedData(
                    resolvedId
                        ? { ...data, id: String(resolvedId), Id: String(resolvedId), organization_id: String(resolvedId), OrganizationId: String(resolvedId) }
                        : data
                );
                // If API returned an id (existing org), store it
                if (resolvedId) {
                    setOrganizationId(String(resolvedId));
                    try { localStorage.setItem('organizationId', String(resolvedId)); } catch (e) {}
                }
                setIsNpiValidated(true);
            } else if (response?.is_success === false && response?.status_code === 500) {
                toast.error('Internal Server Error. Please Contact Support Team.');
            } else {
                setValidationError(response?.message || 'NPI validation failed');
            }
        } catch (error) {
            console.error('Error validating NPI:', error);
            setValidationError(error?.message || 'Error validating NPI');
        } finally {
            setValidationLoading(false);
        }
    }, [npiNumber, token]);

    const handleChildSaved = React.useCallback((orgId, savedData) => {
        if (!completedTabs.includes(activeTabIndex)) {
            setCompletedTabs((prev) => [...prev, activeTabIndex]);
        }
        if (orgId) {
            // Ensure we store a primitive id, not an object
            let idToStore = orgId;
            if (typeof orgId === 'object' && orgId !== null) {
                idToStore = orgId.id ?? orgId.Id ?? orgId.organization_id ?? orgId.OrganizationId ?? null;
            }
            if (idToStore != null) {
                // Organization.id is a UUID on the backend — never Number() it.
                const resolvedId = String(idToStore);
                setOrganizationId(resolvedId);
                setPrepopulatedData((prev) => ({
                    ...(prev || {}),
                    ...(savedData || {}),
                    id: resolvedId,
                    Id: resolvedId,
                    organization_id: resolvedId,
                    OrganizationId: resolvedId,
                }));
                try {
                    localStorage.setItem('organizationId', resolvedId);
                } catch (e) {
                    console.error('Failed to write organizationId to localStorage', e);
                }
            } else {
                console.warn('handleChildSaved called with non-id organization value', orgId);
            }
        } else if (savedData) {
            setPrepopulatedData((prev) => ({
                ...(prev || {}),
                ...savedData,
            }));
        }
    }, [activeTabIndex, completedTabs]);

    const handleChildContinue = React.useCallback((orgId, savedData) => {
        handleChildSaved(orgId, savedData);
        const resolveId = (value) => {
            if (!value) return null;
            if (typeof value === 'object' && value !== null) {
                return value.id ?? value.Id ?? value.organization_id ?? value.OrganizationId ?? null;
            }
            return value;
        };

        if (isLastTab) {
            const targetId = resolveId(orgId) || organizationId;
            if (targetId) {
                navigate(APPRoutes.ORGANIZATIONS_VIEW.replace(':id', String(targetId)));
            } else {
                navigate(APPRoutes.ORGANIZATIONS);
            }
        } else {
            setActiveTabIndex((prev) => prev + 1);
        }
    }, [handleChildSaved, isLastTab, navigate, organizationId]);

    React.useEffect(() => {
        return () => {
            try {
                localStorage.removeItem('organizationId');
            } catch (e) {
                console.error('Failed to remove organizationId from localStorage', e);
            }
        };
    }, []);

    React.useEffect(() => {
        if (!isEditing) return;

        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const response = await getOrganizationById(id, token);
                if (!mounted) return;
                if (response?.is_success) {
                    const d = response.data || response;
                    const normalizeAddress = (addr) => {
                        if (!addr) return null;
                        return {
                            Id: addr.Id ?? addr.id ?? null,
                            Street: addr.Street ?? addr.street ?? '',
                            Street2: addr.Street2 ?? addr.street2 ?? '',
                            City: addr.City ?? addr.city ?? '',
                            State: addr.State ?? addr.state ?? '',
                            Country: addr.Country ?? addr.country ?? '',
                            Zipcode: addr.Zipcode ?? addr.zipcode ?? '',
                        };
                    };

                    const resolveIsCredentialing = (data) => {
                        if (!data) return false;
                        const rawVal = data.is_credentialing ?? data.IsCredentialing ?? data.is_credential ?? data.IsCredential ?? data.is_credientailing ?? data.isCredientailing ?? data.isCredentialing;
                        if (typeof rawVal === 'boolean') return rawVal;
                        if (typeof rawVal === 'string') {
                            if (rawVal.toLowerCase() === 'true' || rawVal === '1') return true;
                            if (rawVal.toLowerCase() === 'false' || rawVal === '0') return false;
                        }
                        if (typeof rawVal === 'number') return rawVal === 1;
                        const rawCred = data.cred_mem ?? data.CredMem ?? data.handled_by ?? data.HandledBy ?? data.credMembers ?? data.cred_member ?? data.CredMember;
                        if (Array.isArray(rawCred)) return rawCred.length > 0;
                        if (rawCred && typeof rawCred === 'object') return Object.keys(rawCred).length > 0;
                        if (typeof rawCred === 'number') return true;
                        if (typeof rawCred === 'string') return rawCred.trim().length > 0;
                        return false;
                    };

                    const resolveCredMember = (data) => {
                        if (!data) return '';
                        const rawCred = data.cred_mem ?? data.CredMem ?? data.handled_by ?? data.HandledBy ?? data.credMembers ?? data.cred_member ?? data.CredMember ?? data.handledBy;
                        if (Array.isArray(rawCred)) {
                            if (rawCred.length === 0) return '';
                            const first = rawCred[0];
                            if (first == null) return '';
                            if (typeof first === 'object') {
                                return String(first.value ?? first.id ?? first.Id ?? first.member_id ?? first.MemberId ?? first.userId ?? first.UserId ?? first.handled_by ?? '');
                            }
                            return String(first);
                        }
                        if (rawCred && typeof rawCred === 'object') {
                            return String(rawCred.value ?? rawCred.id ?? rawCred.Id ?? rawCred.member_id ?? rawCred.MemberId ?? rawCred.userId ?? rawCred.UserId ?? rawCred.handled_by ?? rawCred.handledBy ?? '');
                        }
                        if (typeof rawCred === 'number' || typeof rawCred === 'string') {
                            return String(rawCred);
                        }
                        return '';
                    };

                    // Organization.id is a UUID on the backend — never Number() it.
                    const resolvedOrgId = d.Id ?? d.id ?? d.organization_id ?? d.OrganizationId ?? d.org_id ?? d.OrgId ?? id ?? null;
                    const mapped = {
                        id: resolvedOrgId ? String(resolvedOrgId) : null,
                        organization_id: resolvedOrgId ? String(resolvedOrgId) : null,
                        status_id: d.StatusId ?? d.status_id ?? null,
                        group_office_name: d.GroupOfficeName ?? d.group_office_name ?? '',
                        dba: d.Dba ?? d.dba ?? '',
                        email: d.Email ?? d.email ?? d.group_email ?? d.contact_email ?? '',
                        established_on: d.EstablishedOn ?? d.established_on ?? '',
                        phone: d.Phone ?? d.phone ?? '',
                        fax: d.Fax ?? d.fax ?? '',
                        account_id: d.AccountId ?? d.account_id ?? null,
                        pos_list: d.PosList ?? d.pos_list ?? [],
                        cid: d.Cid ?? d.cid ?? '',
                        tax_id: d.TaxId ?? d.tax_id ?? '',
                        group_npi: d.GroupNpi ?? d.group_npi ?? '',
                        medicaid_ptan: d.MedicaidPtan ?? d.medicaid_ptan ?? '',
                        medicaid_group: d.MedicaidGroup ?? d.medicaid_group ?? '',
                        bcbs_group: d.BcbsGroup ?? d.bcbs_group ?? '',
                        mem_list: d.MemList ?? d.mem_list ?? [],
                        is_credentialing: resolveIsCredentialing(d),
                        cred_mem: resolveCredMember(d) || (d.cred_mem ?? d.CredMem ?? d.handled_by ?? d.HandledBy ?? null),
                        handled_by: resolveCredMember(d) || (d.handled_by ?? d.HandledBy ?? d.cred_mem ?? d.CredMem ?? null),
                        mailing_address: normalizeAddress(d.MailingAddress ?? d.mailing_address ?? null),
                        pay_to_address: normalizeAddress(d.PayToAddress ?? d.pay_to_address ?? null),
                        emr_name: d.EmrName ?? d.emr_name ?? null,
                        pm: d.Pm ?? d.pm ?? null,
                        clearing_house: d.ClearingHouse ?? d.clearing_house ?? null,
                        erx: d.Erx ?? d.erx ?? false,
                        drug_injection: d.DrugInjection ?? d.drug_injection ?? false,
                        drug_injection_notes: d.DrugInjectionNotes ?? d.drug_injection_notes ?? null,
                        labs: d.Labs ?? d.labs ?? false,
                        taxonomies: d.Taxonomies ?? d.taxonomies ?? [],
                        pay_to_address_id: d.PayToAddressId ?? d.pay_to_address_id ?? d.pay_to_address?.Id ?? d.pay_to_address?.id ?? null,
                        mailing_address_id: d.MailingAddressId ?? d.mailing_address_id ?? d.mailing_address?.Id ?? d.mailing_address?.id ?? null,
                        avg_monthly_volume: d.AvgMonthlyVolume ?? d.avg_monthly_volume ?? null,
                        avg_monthly_collections: d.AvgMonthlyCollections ?? d.avg_monthly_collections ?? null,
                    };

                    if (resolvedOrgId) {
                        setOrganizationId(String(resolvedOrgId));
                        try {
                            localStorage.setItem('organizationId', String(resolvedOrgId));
                        } catch (e) {}
                    }
                    setPrepopulatedData(mapped);
                }
                else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal Server Error. Please Contact Support Team.');
                }
                else {
                    toast.error(response?.message || 'Failed to load organization');
                }
            } catch (error) {
                console.error('Error fetching organization by id:', error);
                toast.error('Error fetching organization');
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [isEditing, id, token]);

    const handleNextTab = React.useCallback(() => {
        setActiveTabIndex((prev) => Math.min(prev + 1, TAB_ITEMS.length - 1));
    }, []);

    const handlePreviousTab = React.useCallback(() => {
        setActiveTabIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const renderActiveTab = () => {
        switch (activeTabId) {
            case 'client-profile':
                return (
                    <ClientProfile
                        initialData={prepopulatedData}
                        organizationId={organizationId}
                        onSaveSuccess={handleChildSaved}
                        onContinue={handleChildContinue}
                        isEditMode={isEditing}
                    />
                );
            case 'documents':
                return <Documents organizationId={organizationId} onSaveSuccess={handleChildSaved} onContinue={handleChildContinue} />;
            case 'transition-info':
                return <TransitionInfo organizationId={organizationId} onSaveSuccess={handleChildSaved} onContinue={handleChildContinue} />;
            case 'sop':
                return <SOP organizationId={organizationId} clientCid={prepopulatedData?.cid} onSaveSuccess={handleChildSaved} onContinue={handleChildContinue} />;
            case 'reports':
                return <Reports organizationId={organizationId} onSaveSuccess={handleChildSaved} onContinue={handleChildContinue} />;
            default:
                return null;
        }
    };
    if (isEditing && !canEditOrganization) {
        return (
            <div className="container-fluid py-4">
                <div className="p-4 bg-white rounded-4 shadow-sm">
                    <h5 className="mb-2">Permission denied</h5>
                    <p className="text-muted">You do not have permission to edit this organization.</p>
                    <button type="button" className="btn btn-outline-primary" onClick={() => navigate(APPRoutes.ORGANIZATIONS)}>
                        Back to Organizations
                    </button>
                </div>
            </div>
        );
    }

    if (isEditing && loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <DotLottie/>
            </div>
        );
    }

    if (isCreateMode && !canCreateOrganization) {
        return (
            <div className="container-fluid py-4">
                <div className="p-4 bg-white rounded-4 shadow-sm">
                    <h5 className="mb-2">Permission denied</h5>
                    <p className="text-muted">You do not have permission to create organizations.</p>
                    <button type="button" className="btn btn-outline-primary" onClick={() => navigate(APPRoutes.ORGANIZATIONS)}>
                        Back to Organizations
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <OrganizationFormModal
                open={isCreateMode && !isNpiValidated}
                title="Validate NPI Number"
                submitLabel={validationLoading ? 'Validating...' : 'Validate NPI'}
                onClose={() => navigate(APPRoutes.ORGANIZATIONS)}
                onSubmit={(event) => {
                    event.preventDefault();
                    handleValidateNpi();
                }}
                loading={validationLoading}
            >
                <p className="text-secondary small mb-3 mt-0">
                    Enter your 10-digit National Provider Identifier (NPI) to validate and proceed with organization setup.
                </p>
                <div className="mb-0">
                    <input
                        type="text"
                        className="form-control rounded-3 shadow-sm"
                        placeholder="Enter 10-digit NPI Number"
                        value={formatNpiInput(npiNumber)}
                        onChange={(event) => setNpiNumber(normalizeDigits(event.target.value).slice(0, 10))}
                        disabled={validationLoading}
                    />
                    {validationError ? (
                        <div className="form-text text-danger mt-2">{validationError}</div>
                    ) : null}
                </div>
            </OrganizationFormModal>

            <div className="container-fluid p-0">
                {isNpiValidated && (
                    <div className="rounded-4 bg-white p-2 p-md-4 shadow-md border-0">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="d-flex align-items-center gap-3">
                                <button
                                    type="button"
                                    className="btn btn-light border rounded-circle shadow-sm"
                                    onClick={() => navigate(-1)}
                                    style={{ width: 40, height: 40 }}
                                >
                                    &larr;
                                </button>
                                <div>
                                    <h3 className="fw-bold mb-0 hc-text-primary">
                                        {isEditing ? 'Edit Organization' : 'Add Organization'}
                                    </h3>
                                    {loading && isEditing && (
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
                                    )}
                                    <small className="text-muted">Set up organization details across multiple sections</small>
                                </div>
                            </div>
                        </div>
                        {/* Tab Navigation */}
                        <div className="p-3 shadow rounded-4">
                            <ul className="nav nav-pills gap-2 d-flex flex-column flex-md-row flex-wrap w-100">
                                {TAB_ITEMS.map((tab, index) => {
                                    const isCompleted = completedTabs.includes(index);
                                    const isActive = activeTabIndex === index;

                                    return (
                                        <li className="nav-item flex-grow-1" key={tab.id}>
                                            <button
                                                type="button"
                                                className={`nav-link border px-3 py-2 rounded-3 text-capitalize fw-semibold w-100 text-center d-flex align-items-center justify-content-center gap-2 ${
                                                    isActive
                                                        ? 'hc-bg-primary text-white'
                                                        : isCompleted
                                                        ? 'bg-success text-white'
                                                        : 'bg-light text-secondary'
                                                }`}
                                                onClick={() => {
                                                    setActiveTabIndex(index);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {isCompleted && !isActive && (
                                                    <span className="badge bg-white text-success fw-bold p-1 d-inline-flex align-items-center"><MdCheck size={12} /></span>
                                                )}
                                                {tab.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {/* Tab Content */}
                        <div>
                            {loading && activeTabId === 'client-profile' ? (
                                <div className="p-3">Loading organization...</div>
                            ) : (
                                renderActiveTab()
                            )}
                        </div>
                    </div>
                )}

            </div>
        </>
    );
}

